
import React from 'react';
import { useNavigate } from 'react-router-dom';

interface SidebarWidgetsProps {
    user?: any;
    certificates: any[];
    activity: any[];
    arrearsCount?: number;
    arrearsAlerts?: any[];
}


const SidebarWidgets: React.FC<SidebarWidgetsProps> = ({ user, certificates, activity, arrearsCount = 0, arrearsAlerts = [] }) => {
    const navigate = useNavigate();
    const isSuperAdmin = user?.['custom:role'] === 'SUPER_ADMIN' || user?.role === 'SUPER_ADMIN';

    const getCertStatus = (typeKeywords: string[]) => {
        const typeCerts = certificates.filter(c => typeKeywords.some(k => c.type?.toLowerCase().includes(k)));
        const total = typeCerts.length;
        if (total === 0) return { status: 'Pending', count: 'No Data', color: 'yellow', validCount: 0, total: 0 };

        const now = new Date();
        const validCerts = typeCerts.filter(c => new Date(c.expires) > now);
        const validCount = validCerts.length;
        const expiringSoon = typeCerts.some(c => {
            const exp = new Date(c.expires);
            const diff = exp.getTime() - now.getTime();
            const days = Math.ceil(diff / (1000 * 3600 * 24));
            return days > 0 && days <= 30;
        });

        if (validCount === total && !expiringSoon) return { status: 'Valid', count: 'All valid', color: 'green', validCount, total };
        if (validCount === total && expiringSoon) return { status: 'Action', count: 'Expiring soon', color: 'yellow', validCount, total };

        return { status: 'Action', count: `${validCount}/${total} Valid`, color: validCount === 0 ? 'red' : 'yellow', validCount, total };
    };

    const gasStatus = getCertStatus(['gas', 'cp12']);
    const elecStatus = getCertStatus(['electrical', 'eicr']);
    const epcStatus = getCertStatus(['energy', 'epc']);

    const renderStatusCard = (title: string, icon: string, iconColor: string, statusObj: any) => (
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex justify-between items-center">
                <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 bg-${statusObj.color === 'red' ? 'red' : statusObj.color === 'yellow' ? 'yellow' : 'green'}-100 rounded-lg flex items-center justify-center`}>
                        <i className={`fas ${icon} text-${iconColor}`}></i>
                    </div>
                    <div>
                        <h4 className="font-semibold text-gray-900 text-sm">{title}</h4>
                        <p className="text-xs text-gray-600">{statusObj.count}</p>
                    </div>
                </div>
                <span className={`bg-${statusObj.color}-100 text-${statusObj.color}-800 px-3 py-1 rounded-full text-xs font-semibold`}>
                    {statusObj.status}
                </span>
            </div>
        </div>
    );

    return (
        <div className="space-y-6">

            {/* Certificate Status */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Property Health</h3>
                <div className="space-y-4">
                    {/* Rent Arrears Card */}
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex justify-between items-center">
                            <div className="flex items-center space-x-3">
                                <div className={`w-10 h-10 bg-${arrearsCount > 0 ? 'red' : 'green'}-100 rounded-lg flex items-center justify-center`}>
                                    <i className={`fas fa-coins text-${arrearsCount > 0 ? 'red' : 'green'}-600`}></i>
                                </div>
                                <div>
                                    <h4 className="font-semibold text-gray-900 text-sm">Rent Status</h4>
                                    <p className="text-xs text-gray-600">{arrearsCount > 0 ? `${arrearsCount} Tenant(s) Late` : 'All Payments Up-to-Date'}</p>
                                </div>
                            </div>
                            <span className={`bg-${arrearsCount > 0 ? 'red' : 'green'}-100 text-${arrearsCount > 0 ? 'red' : 'green'}-800 px-3 py-1 rounded-full text-xs font-semibold`}>
                                {arrearsCount > 0 ? 'Action' : 'Good'}
                            </span>
                        </div>
                    </div>

                    {renderStatusCard("Gas Safety (CP12)", "fa-fire", "red-600", gasStatus)}
                    {renderStatusCard("Electrical (EICR)", "fa-bolt", "green-600", elecStatus)}
                    {renderStatusCard("Energy (EPC)", "fa-leaf", "green-600", epcStatus)}
                </div>
            </div>



            {/* Recent Activity */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
                <div className="space-y-4">
                    {activity.map((item, idx) => (
                        <div key={idx} className={`bg-${item.type === 'warning' ? 'yellow' : item.type === 'success' ? 'green' : 'blue'}-50 rounded-lg p-4 border border-${item.type === 'warning' ? 'yellow' : item.type === 'success' ? 'green' : 'blue'}-200`}>
                            <div className="flex items-center space-x-3">
                                <div className={`w-8 h-8 bg-${item.type === 'warning' ? 'yellow' : item.type === 'success' ? 'green' : 'blue'}-100 rounded-full flex items-center justify-center flex-shrink-0`}>
                                    <i className={`fas fa-${item.type === 'warning' ? 'exclamation' : item.type === 'success' ? 'check' : 'calendar'} text-${item.type === 'warning' ? 'yellow' : item.type === 'success' ? 'green' : 'blue'}-600 text-xs`}></i>
                                </div>
                                <div className="flex-1">
                                    <p className="font-medium text-gray-900 text-sm">{item.message}</p>
                                    <p className="text-xs text-gray-600">{item.detail}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default SidebarWidgets;
